// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DropdownOption, RouteParamsWithLanguage, StoryLanguageProps } from "../../../../../../types/screensTypes";
import Button from "../../../../../../components/ui/Button";
import { getStoryLanguages, saveStory } from "../../../../../../api/museum";
import Dropdown from "../../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../../../context/useGlobalState";
import stepStyles from "../../../createStep.module.css";

const StoryLanguage: React.FC<StoryLanguageProps> = ({ navigateToNext }) => {
    const { storyId, planId } = useParams<RouteParamsWithLanguage>(); // Get storyId and planId from URL
    const { data, loading, error } = useGlobalState();
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);

    const getStoryLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !storyId) return;
        const languages = await getStoryLanguages({
            token,
            clickableZoneId: storyId,
        });
        const languagesArray: string[] = languages.map((lang: { id: string }) => lang.id);
        setExistingLanguages(languagesArray);
    };

    const saveStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !storyId || !selectedContentLanguage || !planId) {
            return;
        }

        const tempWpt = await saveStory({
            token,
            trackId,
            clickableZoneId: storyId,
            subplanId: planId,
            language: selectedContentLanguage,
        });

        if (tempWpt && tempWpt.clickableZoneId) {
            const id: string = tempWpt.clickableZoneId;
            navigateToNext(id, selectedContentLanguage, planId);
        }
    };

    useEffect(() => {
        if (storyId && storyId !== "") {
            getStoryLanguagesAPI();
        }
    }, [storyId]);

    const languagesFiltered: DropdownOption[] = [
        ...((data.languagesAI as DropdownOption[] | undefined)?.filter((lang) =>
            existingLanguages.includes(lang.value),
        ) || []),
        { label: t("addANewLanguage"), value: "add-new-language" },
    ];

    const languagesUnfiltered: DropdownOption[] =
        (data.languagesAI as DropdownOption[] | undefined)?.filter((lang) => !existingLanguages.includes(lang.value)) ||
        [];

    const handleLanguageChange = (selectedOption: string): void => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true);
            setSelectedContentLanguage(null);
        } else {
            setShowAddLanguageDropdown(false);
            setSelectedContentLanguage(selectedOption);
        }
    };

    return (
        <div>
            <TitleSubtitle title={t("storyLanguage")} subtitle={""} layout="step" />
            <h5>{t("existingLanguages")}</h5>
            <Dropdown
                label=""
                options={languagesFiltered}
                onChange={handleLanguageChange}
                loading={loading}
                error={error ? true : false}
                placeholder={t("language")}
                value={selectedContentLanguage}
            />
            {showAddLanguageDropdown && (
                <>
                    <h5>{t("selectANewLanguage")}</h5>
                    <Dropdown
                        label=""
                        options={languagesUnfiltered}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                    />
                </>
            )}
            <div className={stepStyles.stepButton}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={async () => {
                        await saveStoryAPI();
                    }}
                />
            </div>
        </div>
    );
};

export default StoryLanguage;
